import React, { useEffect, useState } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import useData from "../../hooks/useData";

const provinces = [
  { id: "BC", name: "British Columbia" },
  { id: "AB", name: "Alberta" },
  { id: "SK", name: "Saskatchewan" },
  { id: "MB", name: "Manitoba" },
  { id: "ON", name: "Ontario" },
  { id: "QC", name: "Quebec" },
  { id: "NB", name: "New Brunswick" },
  { id: "NS", name: "Nova Scotia" },
  { id: "NL", name: "Newfoundland and Labrador" },
  { id: "PE", name: "Prince Edward Island" },
  { id: "NT", name: "Northwest Territories" },
  { id: "YT", name: "Yukon" },
  { id: "NU", name: "Nunavut" },
];

const SkuScopeFilters = ({ retailLevel, countryLevel, clientId, onChange }) => {
  const { getFromServer, executeAction } = useData();

  const [retailers, setRetailers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [selectedBanner, setSelectedBanner] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("all");

  useEffect(() => {
    if (retailLevel === "retailer") {
      loadRetailers();
    } else if (retailLevel === "banner") {
      loadBanners();
    } else if (retailLevel === "location" || retailLevel === "account") {
      loadLocations();
    }
  }, [retailLevel, clientId]);

  useEffect(() => {
    const filters = {};
    if (selectedRetailer) filters.retailerId = selectedRetailer;
    if (selectedBanner) filters.bannerId = selectedBanner;
    if (selectedLocation) filters.locationId = selectedLocation;
    if (selectedProvince !== "all") filters.province = selectedProvince;

    onChange(filters);
  }, [selectedRetailer, selectedBanner, selectedLocation, selectedProvince]);

  const loadRetailers = async () => {
    try {
      const results = await executeAction({
        entityName: "Retailer",
        action: "getRetailersForClientListings",
        data: { clientId },
      });
      if (results.isSuccessful) {
        setRetailers(results.data || []);
      } else {
        setRetailers([]);
      }
    } catch (err) {
      setRetailers([]);
    }
  };

  const loadBanners = async () => {
    try {
      const results = await executeAction({
        entityName: "Banner",
        action: "getBannersForClientListings",
        data: { clientId },
      });
      if (results.isSuccessful) {
        setBanners(results.data || []);
      } else {
        setBanners([]);
      }
    } catch (err) {
      setBanners([]);
    }
  };

  const loadLocations = async () => {
    try {
      const results = await executeAction({
        entityName: "Location",
        action: "getLocationsForClientListings",
        data: { clientId },
      });
      if (results.isSuccessful) {
        setLocations(results.data || []);
      } else {
        setLocations([]);
      }
    } catch (err) {
      setLocations([]);
    }
  };

  if (retailLevel === "national" && countryLevel === "national") {
    return null;
  }

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      {retailLevel === "retailer" && (
        <FormControl
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel>Retailer</InputLabel>
          <Select
            value={selectedRetailer}
            label="Retailer"
            onChange={(e) => setSelectedRetailer(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {retailers.map((r) => (
              <MenuItem
                key={r.id}
                value={r.id}
              >
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {retailLevel === "banner" && (
        <FormControl
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel>Banner</InputLabel>
          <Select
            value={selectedBanner}
            label="Banner"
            onChange={(e) => setSelectedBanner(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {banners.map((b) => (
              <MenuItem
                key={b.id}
                value={b.id}
              >
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(retailLevel === "location" || retailLevel === "account") && (
        <FormControl
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel>Location</InputLabel>
          <Select
            value={selectedLocation}
            label="Location"
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((l) => (
              <MenuItem
                key={l.id}
                value={l.id}
              >
                {l.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {countryLevel === "provincial" && (
        <FormControl
          size="small"
          sx={{ minWidth: 200 }}
        >
          <InputLabel>Province</InputLabel>
          <Select
            value={selectedProvince}
            label="Province"
            onChange={(e) => setSelectedProvince(e.target.value)}
          >
            <MenuItem value="all">All Provinces</MenuItem>
            {provinces.map((p) => (
              <MenuItem
                key={p.id}
                value={p.id}
              >
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default SkuScopeFilters;
